import { useMutation, useQueryClient } from "@tanstack/react-query";
import { triggerNotification } from "components/TheComponents/Notification";
import { useAppContext } from "contexts";
import { getErrorMessageFromResponse } from "helpers/apiHelpers";
import { ExceptionHandlingType } from "types/apiServiceTypes";

type UpdateLeaderboardDto = {
  casinoProvider: string;
  startDate: string | null;
  durationDays: string | number;
  maxPublicUsersCount: string | number;
  prize1: string | number;
  prize2: string | number;
  prize3: string | number;
  randomPrizeThreshold: number | null;
  additionalPrizes: { prizeNumber: number; amount: number }[];
  randomPrizes: { prizeNumber: number; amount: number }[];
  prizeSelectionType: string;
  ticketRate: string | number;
};

export function useUpdateLeaderboardMutation({
  onSuccessHandler,
}: {
  onSuccessHandler?: () => void;
}) {
  const { apiService } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leaderboardId,
      body,
      otp,
    }: {
      leaderboardId: string | number;
      body: UpdateLeaderboardDto;
      otp: string;
    }) => {
      const leaderboard = await apiService.patch(`/leaderboard/${leaderboardId}`, body, {
        init: {
          headers: {
            "x-otp": otp,
          },
        },
        exceptionHandlingType: ExceptionHandlingType.MANUAL,
      });

      return leaderboard;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["future-leaderboards"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["/leaderboard/list"],
      });

      await queryClient.invalidateQueries({
        queryKey: [`/leaderboard/${variables.leaderboardId}`],
      });

      onSuccessHandler?.();

      triggerNotification({
        type: "success",
        text: "Leaderboard successfully updated",
      });
    },
    onError: (error: any) => {
      const statusCode = error?.response?.status || error?.statusCode || error?.status;

      if (statusCode === 409) {
        triggerNotification({
          type: "error",
          text:
            getErrorMessageFromResponse(error) ||
            "Leaderboard with this provider and start date already exists",
        });
        return;
      }

      triggerNotification({
        type: "error",
        text: getErrorMessageFromResponse(error) || "Can't update a leaderboard. Try again later.",
      });
    },
  });
}
